import type { Component } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Form from '~/components/Form'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import Modal from '~/components/Modal'
import Textarea from '~/components/Textarea'

export const newServiceSchema = z.object({
  name: z.string().trim().min(1, 'El nombre es obligatorio'),
  description: z.string().trim().optional().default(''),
  price: z.coerce.number().min(0, 'No puede ser negativo').optional().default(0),
  unit: z.string().trim().min(1, 'La unidad es obligatoria').default('servicio')
})

export type NewServiceValuesType = z.infer<typeof newServiceSchema>

export interface NewServiceModalPropsType {
  isOpen: boolean
  isSubmitting: boolean
  currency: string
  locale: string
  onClose: () => void
  /** Valores ya validados y convertidos por el esquema del modal. */
  onSubmit: (values: NewServiceValuesType) => void | Promise<void>
  /** Subtítulo del modal (dónde se usará el servicio). */
  subtitle?: string
}

/** Alta rápida de un servicio del catálogo, compartida por cotizaciones y cuentas de cobro. */
const NewServiceModal: Component<NewServiceModalPropsType> = props => {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm">
      <Modal.Header>
        <h2 class="text-lg font-bold text-foreground">Nuevo Servicio</h2>
        <p class="text-sm text-foreground-subtle">{props.subtitle ?? 'Queda en tu catálogo y se añade al documento'}</p>
      </Modal.Header>
      <Modal.Body>
        <Form
          id="new-service-form"
          validator={newServiceSchema}
          initialValues={{ name: '', description: '', price: 0, unit: 'servicio' }}
          onSubmit={(_formData, values) => props.onSubmit(values as NewServiceValuesType)}
        >
          <div class="space-y-4">
            <Input name="name" label="Nombre del servicio" required placeholder="Desarrollo de aplicación web" />
            <Textarea name="description" label="Descripción" rows={2} placeholder="Qué incluye" />
            <div class="grid grid-cols-2 gap-3">
              <MaskInput name="price" label="Precio desde" maskType="currency" currency={props.currency} locale={props.locale} />
              <Input name="unit" label="Unidad" placeholder="servicio, hora..." />
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="soft" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="new-service-form" disabled={props.isSubmitting}>
            {props.isSubmitting ? 'Creando...' : 'Crear y añadir'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default NewServiceModal
